import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { PasswordInput, isPasswordValid } from "@/components/forms/PasswordInput";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <SiteShell>
      <section className="container-page py-20 max-w-lg" data-testid="forgot-password-root">
        <h1 className="font-serif text-3xl tracking-tight">Reset your password</h1>
        <p className="mt-2 text-muted-foreground">Enter your email and we'll send you a reset link.</p>
        {sent ? (
          <div className="mt-8 card-soft">
            <h3 className="font-serif text-xl">Check your inbox</h3>
            <p className="mt-2 text-sm text-muted-foreground">If that email is associated with a Last Bookmark account, a reset link is on its way. The link expires in 30 minutes.</p>
            <Link to="/login" className="btn-primary mt-6 inline-flex">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 card-soft space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Email</label>
              <input type="email" className="sb-input" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email" placeholder="you@lastbookmark.app" />
            </div>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="forgot-submit">
              {saving ? "Sending…" : "Send reset link"}
            </button>
            <div className="text-center text-sm">
              <Link to="/login" className="link-underline">Back to sign in</Link>
            </div>
          </form>
        )}
      </section>
    </SiteShell>
  );
}

export function ResetPassword() {
  const [search] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const token = search.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid(password)) {
      toast.error("Please meet every password requirement below.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — please sign in.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <SiteShell>
      <section className="container-page py-20 max-w-lg" data-testid="reset-password-root">
        <h1 className="font-serif text-3xl tracking-tight">Choose a new password</h1>
        {!token && <p className="mt-3 text-sm text-destructive">This reset link is missing its token. Request a new one.</p>}
        {done ? (
          <div className="mt-8 card-soft">
            <h3 className="font-serif text-xl">Password updated</h3>
            <p className="mt-2 text-sm text-muted-foreground">Sign in with your new password to continue.</p>
            <Link to="/login" className="btn-primary mt-6 inline-flex">Go to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 card-soft space-y-4">
            <PasswordInput
              value={password}
              onChange={setPassword}
              label="New password"
              placeholder="Choose a strong password"
              required
              autoComplete="new-password"
              showRules
              testId="reset-password-input"
              confirmValue={confirmPassword}
              onConfirmChange={setConfirmPassword}
              confirmLabel="Confirm new password"
              confirmTestId="reset-confirm-password"
            />
            <button type="submit" disabled={saving || !token} className="btn-primary w-full" data-testid="reset-submit">
              {saving ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </section>
    </SiteShell>
  );
}
